import type { Ref } from 'vue';

/**
 * Pinch-to-zoom and pan, scoped to one element.
 *
 * The browser's own pinch zooms the visual viewport — the whole page, chrome and
 * drawer included — which is the wrong gesture when what the user wants to enlarge is
 * a document sitting inside a sheet. There is no way to ask for a "zoom this element
 * only" behaviour, so the gesture is taken over here: `touch-action` tells the browser
 * to keep its hands off, and the transform is applied to the content ourselves.
 *
 * Scoped deliberately to touch input. A trackpad pinch arrives as a ctrl+wheel event
 * and a mouse has no pinch at all; desktop keeps the browser's page zoom, which is the
 * right tool there.
 */

export interface PinchZoomOptions {
    /** Upper bound for the zoom factor. */
    maxScale?: number;
    /** Zoom a double-tap jumps to, and back down from. */
    doubleTapScale?: number;
}

export interface PinchZoom {
    scale: Ref<number>;
    isZoomed: Ref<boolean>;
    /** Bind to the moving element: the transform, plus the `touch-action` the state needs. */
    contentStyle: Ref<Record<string, string>>;
    viewportStyle: Ref<Record<string, string>>;
    reset: () => void;
}

const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_SLOP = 24;

const distance = (a: Touch, b: Touch) => Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

export default function usePinchZoom(viewport: Ref<HTMLElement | null>, content: Ref<HTMLElement | null>, options: PinchZoomOptions = {}): PinchZoom {
    const maxScale = options.maxScale ?? 4;
    const doubleTapScale = options.doubleTapScale ?? 2.5;

    const scale = ref(1);
    const x = ref(0);
    const y = ref(0);
    const isZoomed = computed(() => scale.value > 1);

    // Gesture bookkeeping. Plain variables rather than refs: nothing renders from them,
    // and a touchmove must not cost a re-render per frame.
    let startDistance = 0;
    let startScale = 1;
    let startX = 0;
    let startY = 0;
    let panPointerX = 0;
    let panPointerY = 0;
    let panning = false;
    let lastTapAt = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    /**
     * The content's layout size at scale 1, which is what the transform multiplies. Read
     * from the element rather than passed in, so the caller stays free to resize it (this
     * document is itself fitted to the column width by a container query).
     */
    const contentSize = () => {
        const el = content.value;
        if (!el) return { width: 0, height: 0 };
        return { width: el.offsetWidth, height: el.offsetHeight };
    };

    /**
     * Keeps the document inside its frame: centred on any axis where it is smaller than
     * the frame, and otherwise never dragged past its own edges — the elastic overscroll
     * of a native image viewer is not worth the extra state here.
     */
    const clamp = () => {
        const frame = viewport.value?.getBoundingClientRect();
        if (!frame) return;
        const { width, height } = contentSize();
        const scaledWidth = width * scale.value;
        const scaledHeight = height * scale.value;

        x.value = scaledWidth <= frame.width ? (frame.width - scaledWidth) / 2 : Math.min(0, Math.max(frame.width - scaledWidth, x.value));
        y.value = scaledHeight <= frame.height ? (frame.height - scaledHeight) / 2 : Math.min(0, Math.max(frame.height - scaledHeight, y.value));
    };

    /**
     * Zooms around a point given in viewport coordinates, so whatever is under the
     * fingers stays under the fingers — the property that makes a pinch feel attached to
     * the content instead of to the frame.
     */
    const zoomTo = (next: number, focalX: number, focalY: number) => {
        const frame = viewport.value?.getBoundingClientRect();
        if (!frame) return;
        const clamped = Math.min(maxScale, Math.max(1, next));
        const ratio = clamped / scale.value;
        const localX = focalX - frame.left;
        const localY = focalY - frame.top;

        x.value = localX - (localX - x.value) * ratio;
        y.value = localY - (localY - y.value) * ratio;
        scale.value = clamped;
        clamp();
    };

    const reset = () => {
        scale.value = 1;
        clamp();
    };

    /**
     * Hides a gesture that is ours from the sheet around us. `UiActionSheet`'s drawer
     * reads touches to drag itself down, and a pinch — two fingers, one of them moving
     * down — looks exactly like that dismiss: the first pinch closed the drawer instead
     * of zooming. Stopping propagation rather than raising `touch-action` to `none`
     * keeps the plain one-finger drag on the document scrolling the sheet as before.
     */
    const keepForUs = (event: TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const onTouchStart = (event: TouchEvent) => {
        if (event.touches.length === 2) {
            panning = false;
            startDistance = distance(event.touches[0], event.touches[1]);
            startScale = scale.value;
            keepForUs(event);
            return;
        }

        if (event.touches.length !== 1) return;

        const touch = event.touches[0];
        const now = Date.now();
        const isDoubleTap = now - lastTapAt < DOUBLE_TAP_DELAY && Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY) < DOUBLE_TAP_SLOP;

        if (isDoubleTap) {
            lastTapAt = 0;
            zoomTo(isZoomed.value ? 1 : doubleTapScale, touch.clientX, touch.clientY);
            keepForUs(event);
            return;
        }

        lastTapAt = now;
        lastTapX = touch.clientX;
        lastTapY = touch.clientY;

        // At rest the sheet owns the vertical drag, so a one-finger pan is only ours once
        // there is something to pan — otherwise the document would trap the sheet's scroll.
        if (!isZoomed.value) return;
        panning = true;
        panPointerX = touch.clientX;
        panPointerY = touch.clientY;
        startX = x.value;
        startY = y.value;
    };

    const onTouchMove = (event: TouchEvent) => {
        if (event.touches.length === 2) {
            const current = distance(event.touches[0], event.touches[1]);
            if (!startDistance) return;
            const focalX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
            const focalY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
            zoomTo(startScale * (current / startDistance), focalX, focalY);
            keepForUs(event);
            return;
        }

        if (!panning || event.touches.length !== 1) return;
        const touch = event.touches[0];
        x.value = startX + (touch.clientX - panPointerX);
        y.value = startY + (touch.clientY - panPointerY);
        clamp();
        keepForUs(event);
    };

    const onTouchEnd = (event: TouchEvent) => {
        if (event.touches.length === 0) {
            panning = false;
            startDistance = 0;
        }
    };

    // Safari's own pinch runs off these non-standard events and ignores `touch-action`
    // for them, so page zoom survives everything above unless they are cancelled here.
    // Bound to the element only — never to the document, which would take pinch zoom
    // away from the whole app.
    const onGesture = (event: Event) => event.preventDefault();

    const bind = (el: HTMLElement) => {
        el.addEventListener('touchstart', onTouchStart, { passive: false });
        el.addEventListener('touchmove', onTouchMove, { passive: false });
        el.addEventListener('touchend', onTouchEnd);
        el.addEventListener('touchcancel', onTouchEnd);
        el.addEventListener('gesturestart', onGesture);
        el.addEventListener('gesturechange', onGesture);
        el.addEventListener('gestureend', onGesture);
    };

    const unbind = (el: HTMLElement) => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchmove', onTouchMove);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('touchcancel', onTouchEnd);
        el.removeEventListener('gesturestart', onGesture);
        el.removeEventListener('gesturechange', onGesture);
        el.removeEventListener('gestureend', onGesture);
    };

    watch(
        viewport,
        (el, previous) => {
            if (previous) unbind(previous);
            if (el) bind(el);
        },
        { immediate: true }
    );

    onUnmounted(() => {
        if (viewport.value) unbind(viewport.value);
    });

    const contentStyle = computed(() => ({
        transform: `translate3d(${x.value}px, ${y.value}px, 0) scale(${scale.value})`,
        transformOrigin: 'top left',
    }));

    // At rest this stays out of the way entirely. `pan-y` looked like the tighter
    // choice — it denies the browser the pinch while keeping the vertical scroll — but
    // it also hands the browser the vertical drag to perform on its own, without
    // waiting for JS. The sheet around us moves itself on `touchmove` and calls
    // `preventDefault()` to suppress the scroll underneath, and that call cannot
    // reclaim a pan the browser was already told it could keep: the drawer stopped
    // following the finger, so its drag-to-dismiss lost its animation. Under `auto`
    // the browser waits for these non-passive listeners, so the pinch is still ours
    // (plus Safari's `gesture*`, cancelled above) and every other gesture behaves as
    // it does in any other drawer. `none` once zoomed, where the one-finger drag has
    // to pan the document instead of dragging the sheet.
    const viewportStyle = computed(() => ({
        touchAction: isZoomed.value ? 'none' : 'auto',
    }));

    return { scale, isZoomed, contentStyle, viewportStyle, reset };
}
